import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, Input, Modal, Select, Space, Tag, Typography, message } from "antd";
import { ApiOutlined, LinkOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { flightLogRecognitionApi } from "./api";

const { Text } = Typography;

// Flight Log 航班绑定与燃油报告同步（migration 295）：核对抽屉内的
// 「关联航班 (FOP)」卡片（T08.2 / AC-S1-05 / AC-S2-01~08 / AC-S4-02 / C3）。
// 匹配/同步状态徽标 + 航班快照（UTC 时刻）+ 候选切换与手动搜索 +
// 无对应航班勾选（受控，状态提升到 Panel 供确认提交）+
// 已确认记录换绑/补绑（预览 3 油量值）与 pending_resync 手动重同步。

const MATCH_STATUS_META = {
    auto_matched: { label: "自动匹配", color: "blue" },
    manual_matched: { label: "手动匹配", color: "geekblue" },
    unmatched: { label: "未匹配", color: "default" },
    no_flight_needed: { label: "无需匹配", color: "purple" },
};

const SYNC_STATUS_META = {
    corrected: { label: "已纠正", color: "green" },
    no_change: { label: "无变化", color: "default" },
    skipped_no_report: { label: "无报告已跳过", color: "orange" },
    skipped_no_fop: { label: "无对应航班已跳过", color: "purple" },
    reverted: { label: "已回退", color: "volcano" },
    pending_resync: { label: "待补同步", color: "red" },
};

const FUEL_FIELD_LABELS = {
    fuel_uplift_kg: "加油 kg",
    out_fuel_kg: "OOOI OUT",
    in_fuel_kg: "OOOI IN",
    block_fuel_kg: "BH 油耗",
    flight_fuel_kg: "FH 油耗",
    previous_fuel_kg: "加油前",
    data_completeness: "完整度",
    status: "状态",
    out_fuel_kg_derived: "推算 IN",
};

const FUEL_PREVIEW_FIELDS = [
    ["actual_uplift_kg", "Fuel Uplift（加油量）"],
    ["off_block_fuel_kg", "Fuel KG (Off Block)（撤轮挡油量）"],
    ["on_block_fuel_kg", "On Block Fuel KG（挡轮挡油量）"],
];

const getErrorMessage = (err) => err?.response?.data?.detail || err?.message || "请求失败";

const fmtVal = (v) => (v === null || v === undefined || v === "" ? "空" : String(v));

const fmtUtcTime = (iso) => {
    if (!iso) return "-";
    const m = String(iso).match(/T(\d{2}:\d{2})/);
    return m ? `${m[1]} UTC` : `${iso} UTC`;
};

const candidateLabel = (c) => `${c.flight_number || "?"} · ${c.flight_date || "?"} ${c.departure_airport || "?"}→${c.arrival_airport || "?"}${c.aircraft_reg ? ` · ${c.aircraft_reg}` : ""}`;

const SnapshotLine = ({ label, value }) => (
    <div style={{ fontSize: 12 }}>
        <Text type="secondary">{label}：</Text>
        <Text>{value ?? "-"}</Text>
    </div>
);

export const showFlightLogSyncSummary = (title, correction, revert) => {
    const reports = [...(revert?.reports || []).map((r) => ({ ...r, _kind: "revert" })), ...(correction?.reports || []).map((r) => ({ ...r, _kind: "correction" }))];
    const statusText = correction?.status ? SYNC_STATUS_META[correction.status]?.label || correction.status : null;
    Modal.info({
        title,
        width: 520,
        content: (
            <div>
                {statusText && (
                    <div style={{ marginBottom: 6 }}>
                        <Tag color={SYNC_STATUS_META[correction.status]?.color || "default"}>{statusText}</Tag>
                        {correction.out_skipped_invalid_range && (
                            <Text type="warning" style={{ fontSize: 12 }}>
                                推出油量（Off Block）≤ 落地油量（On Block），已跳过 OUT 同步，请人工核对
                            </Text>
                        )}
                    </div>
                )}
                {reports.length === 0 && <Text type="secondary">无报告明细</Text>}
                {reports.map((r) => (
                    <div key={`${r._kind}-${r.fuel_report_id}`} style={{ marginBottom: 10 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {r._kind === "revert" ? "回退" : "同步"} · 报告 {String(r.fuel_report_id).slice(0, 8)}…
                        </Text>
                        {Object.entries(r.changes || {}).map(([f, c]) => (
                            <div key={f} style={{ fontSize: 13 }}>
                                {FUEL_FIELD_LABELS[f] || f}：{fmtVal(c?.old)} → {fmtVal(c?.new)}
                            </div>
                        ))}
                        {Object.entries(r.skipped || {}).map(([f, s]) => (
                            <div key={f} style={{ fontSize: 13, color: "#fa8c16" }}>
                                {FUEL_FIELD_LABELS[f] || f}：{s?.reason === "modified_after_sync" ? "已被人工修改，保留人工值" : "已跳过"}
                                {s?.current !== undefined ? `（当前 ${fmtVal(s.current)}）` : ""}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        ),
    });
};

/**
 * 核对抽屉「关联航班 (FOP)」卡片。
 *
 * 受控状态：selectedFopId / noFlightNeeded 由 Panel 持有（确认复核提交时随 review
 * payload 透传后端）；已确认记录的换绑/补绑/重同步在本组件内直接调 API 并通过
 * onRecordUpdated / onFuelReportMutated 回传。
 */
const buildDefaultSearchForm = (record) => ({
    flight_no: record?.flight_no || "",
    aircraft_reg: record?.normalized_aircraft_reg || record?.aircraft_reg || "",
    departure_airport: record?.from_iata || "",
    arrival_airport: record?.to_iata || "",
});

const FlightLogFlightBindSection = ({ record, selectedFopId, noFlightNeeded, onChangeSelectedFopId, onChangeNoFlightNeeded, onRecordUpdated, onFuelReportMutated }) => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchForm, setSearchForm] = useState(() => buildDefaultSearchForm(record));
    const [acting, setActing] = useState(false);

    const isConfirmed = record?.status === "confirmed";
    const matchStatus = record?.flight_match_status || "unmatched";
    const correction = record?.correction_result && typeof record.correction_result === "object" ? record.correction_result : {};
    const rawSnapshot = record?.flight_info_snapshot && typeof record.flight_info_snapshot === "object" ? record.flight_info_snapshot : null;
    const snapshot = rawSnapshot && (rawSnapshot.flight_operation_id || rawSnapshot.flight_number) ? rawSnapshot : null;
    const candidates = useMemo(() => (Array.isArray(record?.flight_match_candidates?.candidates) ? record.flight_match_candidates.candidates : []), [record?.flight_match_candidates]);
    const pendingResync = correction.status === "pending_resync";
    // D13：存量「已确认」但未绑航班（历史记录），提供补绑入口
    const legacyUnbound = isConfirmed && !record?.flight_operation_id && matchStatus !== "no_flight_needed";
    const currentFopId = selectedFopId ?? record?.flight_operation_id ?? null;

    // 切换不同记录时重置并预填搜索条件
    const defaultFlightNo = record?.flight_no || "";
    const defaultAircraftReg = record?.normalized_aircraft_reg || record?.aircraft_reg || "";
    const defaultDep = record?.from_iata || "";
    const defaultArr = record?.to_iata || "";
    React.useEffect(() => {
        setSearchOpen(false);
        setSearchResults([]);
        setSearchForm({
            flight_no: defaultFlightNo,
            aircraft_reg: defaultAircraftReg,
            departure_airport: defaultDep,
            arrival_airport: defaultArr,
        });
    }, [record?.id, defaultFlightNo, defaultAircraftReg, defaultDep, defaultArr]);

    const runSearch = async (formValues) => {
        if (!record?.id) return;
        const targetForm = formValues || searchForm;
        const hasCond = Object.values(targetForm).some((v) => v && String(v).trim());
        if (!hasCond) {
            message.warning("至少输入一个搜索条件（航班号 / 机号 / 出发站 / 到达站）");
            return;
        }
        setSearching(true);
        try {
            const res = await flightLogRecognitionApi.getFlightCandidates(record.id, {
                flight_no: targetForm.flight_no || undefined,
                aircraft_reg: targetForm.aircraft_reg || undefined,
                departure_airport: targetForm.departure_airport || undefined,
                arrival_airport: targetForm.arrival_airport || undefined,
                limit: 20,
            });
            setSearchResults(res.data?.items || []);
        } catch (err) {
            message.error(`搜索航班失败：${getErrorMessage(err)}`);
        } finally {
            setSearching(false);
        }
    };

    const handleSearch = () => runSearch(searchForm);

    const handleToggleSearch = () => {
        setSearchOpen((prev) => {
            const next = !prev;
            if (next) {
                const hasAnyInput = Object.values(searchForm).some((v) => v && String(v).trim());
                const effectiveForm = hasAnyInput ? searchForm : buildDefaultSearchForm(record);
                if (!hasAnyInput) {
                    setSearchForm(effectiveForm);
                }
                if (searchResults.length === 0 && Object.values(effectiveForm).some((v) => v && String(v).trim())) {
                    runSearch(effectiveForm);
                }
            }
            return next;
        });
    };

    // 已确认记录换绑/补绑：Modal 预览即将同步的 3 个油量值后调 rebind-flight（AC-S2-06）
    const confirmRebind = (fop) => {
        // D17（AC-S2-07 / T08）：推出油量 ≤ 落地油量时在确认弹窗内提前预警
        const offBlock = record?.off_block_fuel_kg;
        const onBlock = record?.on_block_fuel_kg;
        const d17Invalid = offBlock != null && onBlock != null && Number(offBlock) <= Number(onBlock);
        const preview = (
            <div>
                <div style={{ marginBottom: 8 }}>
                    目标航班：<Text strong>{candidateLabel(fop)}</Text>
                </div>
                <div style={{ marginBottom: 8 }}>确认后将按以下单据值同步该航班燃油报告（空值不覆盖）：</div>
                {FUEL_PREVIEW_FIELDS.map(([field, label]) => (
                    <div key={field} style={{ fontSize: 13 }}>
                        {label}：{fmtVal(record?.[field])}
                    </div>
                ))}
                {d17Invalid && (
                    <div style={{ marginTop: 8, color: "#fa8c16", fontWeight: 600, fontSize: 13 }}>
                        注意：推出油量（Off Block {String(offBlock)}）≤ 落地油量（On Block {String(onBlock)}）， 疑似历史票据口径或识别异常；确认后将自动跳过 OUT 同步并保留报告当前值，请人工核对后再确认。
                    </div>
                )}
                {record?.flight_operation_id && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                        原绑定航班的燃油报告将先回退本单修正，再同步新航班。
                    </Text>
                )}
            </div>
        );
        Modal.confirm({
            title: record?.flight_operation_id ? "更换绑定航班" : "补绑航班",
            content: preview,
            okText: "确认绑定",
            cancelText: "取消",
            onOk: async () => {
                setActing(true);
                try {
                    const res = await flightLogRecognitionApi.rebindFlight(record.id, {
                        flight_operation_id: fop.flight_operation_id,
                    });
                    const data = res.data || {};
                    if (onRecordUpdated) onRecordUpdated(data.record);
                    if (onFuelReportMutated) onFuelReportMutated();
                    showFlightLogSyncSummary("航班换绑与燃油报告同步结果", data.fuel_report_correction, data.fuel_report_revert);
                } catch (err) {
                    message.error(getErrorMessage(err));
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handleRebindNoFlight = () => {
        Modal.confirm({
            title: "标记为无对应航班（仅确认归档）",
            content: "将解除当前航班绑定（如有）、回退本单对燃油报告的修正，且不同步任何燃油报告；适用于地面滑回或系统未录入航班的单据。",
            okText: "确认标记",
            cancelText: "取消",
            onOk: async () => {
                setActing(true);
                try {
                    const res = await flightLogRecognitionApi.rebindFlight(record.id, { no_flight_needed: true });
                    const data = res.data || {};
                    if (onRecordUpdated) onRecordUpdated(data.record);
                    if (onFuelReportMutated) onFuelReportMutated();
                    showFlightLogSyncSummary("燃油报告回退结果", null, data.fuel_report_revert);
                } catch (err) {
                    message.error(getErrorMessage(err));
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handleResync = async () => {
        setActing(true);
        try {
            const res = await flightLogRecognitionApi.resyncFuelReport(record.id);
            const correctionData = res.data?.fuel_report_correction;
            if (onRecordUpdated) onRecordUpdated(null);
            if (onFuelReportMutated) onFuelReportMutated();
            showFlightLogSyncSummary("重新同步燃油报告结果", correctionData, null);
        } catch (err) {
            message.error(`重新同步失败：${getErrorMessage(err)}`);
        } finally {
            setActing(false);
        }
    };

    const matchMeta = legacyUnbound ? { label: "存量未绑", color: "gold" } : MATCH_STATUS_META[matchStatus] || MATCH_STATUS_META.unmatched;
    const syncMeta =
        matchStatus === "no_flight_needed" && correction.status === "skipped_no_fop"
            ? null
            : SYNC_STATUS_META[correction.status];

    const activeSnapshot = useMemo(() => {
        if (currentFopId) {
            const fromCandidate = candidates.find((c) => c.flight_operation_id === currentFopId);
            if (fromCandidate) return fromCandidate;
            const fromSearch = searchResults.find((c) => c.flight_operation_id === currentFopId);
            if (fromSearch) return fromSearch;
            if (snapshot && snapshot.flight_operation_id === currentFopId) return snapshot;
        }
        return snapshot;
    }, [currentFopId, candidates, searchResults, snapshot]);

    const selectOptions = [
        ...candidates.map((c) => ({
            value: c.flight_operation_id,
            label: candidateLabel(c),
        })),
        ...(currentFopId && !candidates.some((c) => c.flight_operation_id === currentFopId) && snapshot?.flight_number ? [{ value: currentFopId, label: candidateLabel(snapshot) }] : []),
        ...searchResults.filter((c) => !candidates.some((x) => x.flight_operation_id === c.flight_operation_id)).map((c) => ({ value: c.flight_operation_id, label: candidateLabel(c) })),
    ];

    return (
        <Card
            size="small"
            title={
                <Space size={6}>
                    <LinkOutlined />
                    关联航班 (FOP)
                </Space>
            }
            extra={
                <Space size={4} wrap>
                    <Tag color={matchMeta.color} style={{ margin: 0 }}>
                        {matchMeta.label}
                    </Tag>
                    {syncMeta && (
                        <Tag color={syncMeta.color} style={{ margin: 0 }}>
                            {syncMeta.label}
                        </Tag>
                    )}
                </Space>
            }
        >
            {pendingResync && (
                <Alert
                    showIcon
                    type="error"
                    style={{ marginBottom: 8 }}
                    message="上次自动补同步失败（待补同步）"
                    description={
                        <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: 12 }}>{correction.error || "自动补同步发生异常，可手动重试；不影响邮件入库与航班匹配。"}</Text>
                            <Button size="small" icon={<SyncOutlined />} loading={acting} onClick={handleResync}>
                                重新同步燃油报告
                            </Button>
                        </Space>
                    }
                />
            )}
            {correction.out_skipped_invalid_range && <Alert showIcon type="warning" style={{ marginBottom: 8 }} message="推出油量（Off Block）≤ 落地油量（On Block），已跳过 OUT 同步" description="疑似历史票据口径或识别异常，请人工核对 Fuel KG (Off Block) 与 On Block Fuel KG。" />}

            {activeSnapshot && (
                <div style={{ marginBottom: 8, padding: "6px 10px", background: "#fafafa", borderRadius: 4 }}>
                    <SnapshotLine label="航班号" value={activeSnapshot.flight_number} />
                    <SnapshotLine label="日期 UTC" value={activeSnapshot.flight_date} />
                    <SnapshotLine label="机号" value={activeSnapshot.aircraft_reg} />
                    <SnapshotLine label="航段" value={`${activeSnapshot.departure_airport || "?"} → ${activeSnapshot.arrival_airport || "?"}`} />
                    <SnapshotLine label="STD" value={fmtUtcTime(activeSnapshot.std_utc)} />
                    <SnapshotLine label="ATD" value={fmtUtcTime(activeSnapshot.atd_utc)} />
                    <SnapshotLine label="STA" value={fmtUtcTime(activeSnapshot.sta_utc)} />
                    <SnapshotLine label="ATA" value={fmtUtcTime(activeSnapshot.ata_utc)} />
                </div>
            )}

            {isConfirmed ? (
                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <Space size={8} wrap>
                        <Button size="small" icon={<LinkOutlined />} loading={acting} onClick={handleToggleSearch}>
                            {record?.flight_operation_id ? "更换绑定航班" : "补绑航班"}
                        </Button>
                        {matchStatus !== "no_flight_needed" && (
                            <Button size="small" onClick={handleRebindNoFlight} loading={acting}>
                                标记无对应航班
                            </Button>
                        )}
                    </Space>
                    {searchOpen && (
                        <div style={{ padding: 8, background: "#fafafa", borderRadius: 4 }}>
                            <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                <Space size={6} wrap>
                                    <Input size="small" placeholder="航班号" style={{ width: 110 }} value={searchForm.flight_no} onChange={(e) => setSearchForm((f) => ({ ...f, flight_no: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                    <Input size="small" placeholder="机号" style={{ width: 100 }} value={searchForm.aircraft_reg} onChange={(e) => setSearchForm((f) => ({ ...f, aircraft_reg: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                    <Input size="small" placeholder="出发站" style={{ width: 88 }} value={searchForm.departure_airport} onChange={(e) => setSearchForm((f) => ({ ...f, departure_airport: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                    <Input size="small" placeholder="到达站" style={{ width: 88 }} value={searchForm.arrival_airport} onChange={(e) => setSearchForm((f) => ({ ...f, arrival_airport: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                    <Button size="small" type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
                                        搜索
                                    </Button>
                                </Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    搜索结果（选定后点击「确认绑定」完成换绑，时刻均为 UTC）：
                                </Text>
                                {(searchResults || []).map((c) => (
                                    <div key={c.flight_operation_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                        <Text style={{ fontSize: 12 }}>
                                            {candidateLabel(c)}
                                            {c.std_utc ? ` · STD ${fmtUtcTime(c.std_utc)}` : ""}
                                            {c.atd_utc ? ` · ATD ${fmtUtcTime(c.atd_utc)}` : ""}
                                        </Text>
                                        <Button size="small" type="link" onClick={() => confirmRebind(c)}>
                                            确认绑定
                                        </Button>
                                    </div>
                                ))}
                                {searchResults.length === 0 && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        暂无搜索结果
                                    </Text>
                                )}
                            </Space>
                        </div>
                    )}
                </Space>
            ) : (
                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <Space size={6} wrap style={{ width: "100%" }}>
                        <Select
                            size="small"
                            allowClear
                            placeholder="选择绑定航班（候选/搜索结果）"
                            style={{ minWidth: 300 }}
                            popupMatchSelectWidth={false}
                            value={currentFopId}
                            options={selectOptions}
                            onChange={(value) => {
                                if (value) onChangeNoFlightNeeded(false);
                                onChangeSelectedFopId(value ?? null);
                            }}
                            notFoundContent="暂无候选，可展开搜索其他航班"
                        />
                        <Button size="small" icon={<ApiOutlined />} onClick={handleToggleSearch}>
                            {searchOpen ? "收起搜索" : "搜索其他航班"}
                        </Button>
                    </Space>
                    {searchOpen && (
                        <div style={{ padding: 8, background: "#fafafa", borderRadius: 4 }}>
                            <Space size={6} wrap>
                                <Input size="small" placeholder="航班号" style={{ width: 110 }} value={searchForm.flight_no} onChange={(e) => setSearchForm((f) => ({ ...f, flight_no: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                <Input size="small" placeholder="机号" style={{ width: 100 }} value={searchForm.aircraft_reg} onChange={(e) => setSearchForm((f) => ({ ...f, aircraft_reg: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                <Input size="small" placeholder="出发站" style={{ width: 88 }} value={searchForm.departure_airport} onChange={(e) => setSearchForm((f) => ({ ...f, departure_airport: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                <Input size="small" placeholder="到达站" style={{ width: 88 }} value={searchForm.arrival_airport} onChange={(e) => setSearchForm((f) => ({ ...f, arrival_airport: e.target.value }))} onPressEnter={handleSearch} allowClear />
                                <Button size="small" type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
                                    搜索
                                </Button>
                            </Space>
                            {(searchResults || []).map((c) => (
                                <div key={c.flight_operation_id} style={{ marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                    <Text style={{ fontSize: 12 }}>
                                        {candidateLabel(c)}
                                        {c.std_utc ? ` · STD ${fmtUtcTime(c.std_utc)}` : ""}
                                    </Text>
                                    <Button
                                        size="small"
                                        type="link"
                                        onClick={() => {
                                            onChangeNoFlightNeeded(false);
                                            onChangeSelectedFopId(c.flight_operation_id);
                                        }}
                                    >
                                        选定
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <Checkbox
                        checked={noFlightNeeded}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) onChangeSelectedFopId(null);
                            onChangeNoFlightNeeded(checked);
                        }}
                    >
                        无对应航班（仅确认归档，不同步燃油报告）
                    </Checkbox>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        适用于地面滑回（Return to Stand）或系统未录入对应 FOP 航班的单据；确认后不占用正常航班绑定名额。
                    </Text>
                </Space>
            )}
        </Card>
    );
};

export default FlightLogFlightBindSection;
